import React from 'react';
import { Button, Form, Grid, Header, Icon, Loader, Popup, Segment, Table } from 'semantic-ui-react';
import axios from 'axios';
import ReactExport from 'react-data-export';
import SearchTableData from '../../util/SearchTableData';
import PaginationFront from '../../util/PaginationFront';
import printReportAsPdf from './reportPdfPrint';
import {
    additionalFilterSectionStyle,
    checkboxFieldStyle,
    filterFieldGridStyle,
    requiredFilterSectionStyle,
} from './reportUiHelpers';

const { ExcelFile } = ReactExport;
const { ExcelSheet } = ReactExport.ExcelFile;

const columnBorderStyle = {
    borderLeft: '1px solid rgba(34, 36, 38, 0.15)',
    borderRight: '1px solid rgba(34, 36, 38, 0.15)',
};

const studyProgramCellStyle = {
    ...columnBorderStyle,
    backgroundColor: '#fff',
};

class CourseStudentsByAcademicYear extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            academicYears: [],
            academicYearId: null,
            organizationalUnitId: null,
            departments: [],
            departmentId: null,
            studyProgramBasics: [],
            studyProgramBasicsAll: [],
            studyProgramBasicId: null,
            data: [],
            results: [],
            loading: false,
            pdfLoading: false,
            reportRequested: false,
            activePage: 1,
            postsPerPage: 20,
            hoveredCourseId: null,
            showDetailedStudentBreakdown: false,
        };
        this.handleInputChange = this.handleInputChange.bind(this);
        this.loadReportFilters = this.loadReportFilters.bind(this);
        this.getData = this.getData.bind(this);
        this.getResults = this.getResults.bind(this);
        this.printPdf = this.printPdf.bind(this);
    }

    componentDidMount() {
        axios.get('/academic-years/current-and-previous/')
            .then(response => this.setState({
                academicYears: response.data.map(year => ({
                    ...year, key: year.id, value: year.id, text: year.name,
                })).reverse(),
            }));

        if (!this.props.requireOrganizationalUnit) this.loadReportFilters();
    }

    loadReportFilters(organizationalUnitId) {
        const studyProgramsUrl = organizationalUnitId
            ? `/study-programs/basic/${organizationalUnitId}` : 'study-programs/basic';
        axios.all([
            axios.post(studyProgramsUrl, {}),
            axios.get('/exams/course-report-department-filters', {
                params: { organizationalUnitId: organizationalUnitId || null },
            }),
        ]).then(axios.spread((studyProgramsResponse, departmentFiltersResponse) => {
            const departmentFilters = departmentFiltersResponse.data;
            const availableStudyProgramBasicIds = departmentFilters
                .map(filter => filter.studyProgramBasicId);
            const studyProgramBasics = studyProgramsResponse.data
                .filter(item => !item.studyProgramBasic.archived &&
                    availableStudyProgramBasicIds.indexOf(item.studyProgramBasic.id) !== -1)
                .map(item => ({
                    key: item.studyProgramBasic.id,
                    value: item.studyProgramBasic.id,
                    text: item.studyProgramBasic.name + (item.studyProgramBasic.comment
                        ? ` (${item.studyProgramBasic.comment})` : ''),
                    description: (item.studyProgramBasic.studyCycles || [])
                        .map(cycle => cycle.displayName)
                        .filter(Boolean)
                        .join(', '),
                    departmentIds: departmentFilters
                        .filter(filter => filter.studyProgramBasicId === item.studyProgramBasic.id)
                        .map(filter => filter.departmentId),
                }));
            const departments = departmentFilters
                .filter((filter, index, filters) => filters.findIndex(item =>
                    item.departmentId === filter.departmentId) === index)
                .map(filter => ({
                    key: filter.departmentId,
                    value: filter.departmentId,
                    text: filter.departmentName,
                }))
                .sort((first, second) => first.text.localeCompare(second.text));
            this.setState({
                studyProgramBasics,
                studyProgramBasicsAll: studyProgramBasics,
                departments,
            });
        })).catch(() => this.setState({
            studyProgramBasics: [],
            studyProgramBasicsAll: [],
            departments: [],
        }));
    }

    handleInputChange(event, { name, value }) {
        if (name === 'organizationalUnitId') {
            this.setState({
                organizationalUnitId: value || null,
                departmentId: null,
                studyProgramBasicId: null,
                studyProgramBasics: [],
                studyProgramBasicsAll: [],
                departments: [],
                data: [],
                results: [],
                reportRequested: false,
                activePage: 1,
            });
            if (value) this.loadReportFilters(value);
            return;
        }
        if (name === 'departmentId') {
            this.setState({
                departmentId: value || null,
                studyProgramBasicId: null,
                studyProgramBasics: value
                    ? this.state.studyProgramBasicsAll.filter(studyProgram =>
                        studyProgram.departmentIds.indexOf(value) !== -1)
                    : this.state.studyProgramBasicsAll,
                data: [],
                results: [],
                reportRequested: false,
                activePage: 1,
            });
            return;
        }
        this.setState({
            [name]: value,
            data: [],
            results: [],
            reportRequested: false,
            activePage: 1,
        });
    }

    getResults(results) {
        this.setState({ results, activePage: 1 });
    }

    getOptionText(options, value) {
        const option = options.find(item => item.value === value);
        return option ? option.text : null;
    }

    getStudyProgramRows(course) {
        if (!course.studyProgramBasics || !course.studyProgramBasics.length) {
            return [{
                name: '—',
                implementations: [{ name: '—', code: null }],
                implementationsText: '—',
                numberOfStudents: 0,
                numberOfRegularStudents: 0,
                numberOfSelfFinancingStudents: 0,
                numberOfPartTimeStudents: 0,
                numberOfDistanceLearningStudents: 0,
                numberOfFirstTimeEnrolledStudents: 0,
                numberOfFirstTimeEnrolledRegularStudents: 0,
                numberOfFirstTimeEnrolledSelfFinancingStudents: 0,
                numberOfFirstTimeEnrolledPartTimeStudents: 0,
                numberOfFirstTimeEnrolledDistanceLearningStudents: 0,
                numberOfRepeatedlyEnrolledStudents: 0,
                numberOfRepeatedlyEnrolledRegularStudents: 0,
                numberOfRepeatedlyEnrolledSelfFinancingStudents: 0,
                numberOfRepeatedlyEnrolledPartTimeStudents: 0,
                numberOfRepeatedlyEnrolledDistanceLearningStudents: 0,
            }];
        }
        const studyProgramsByBasic = {};
        course.studyProgramBasics.forEach((studyProgram) => {
            const key = studyProgram.studyProgramBasicId || studyProgram.name;
            if (!studyProgramsByBasic[key]) {
                studyProgramsByBasic[key] = {
                    name: studyProgram.name,
                    implementations: [],
                    numberOfStudents: studyProgram.numberOfStudents || 0,
                    numberOfRegularStudents: studyProgram.numberOfRegularStudents || 0,
                    numberOfSelfFinancingStudents: studyProgram.numberOfSelfFinancingStudents || 0,
                    numberOfPartTimeStudents: studyProgram.numberOfPartTimeStudents || 0,
                    numberOfDistanceLearningStudents: studyProgram.numberOfDistanceLearningStudents || 0,
                    numberOfFirstTimeEnrolledStudents: studyProgram.numberOfFirstTimeEnrolledStudents || 0,
                    numberOfFirstTimeEnrolledRegularStudents: studyProgram.numberOfFirstTimeEnrolledRegularStudents || 0,
                    numberOfFirstTimeEnrolledSelfFinancingStudents: studyProgram.numberOfFirstTimeEnrolledSelfFinancingStudents || 0,
                    numberOfFirstTimeEnrolledPartTimeStudents: studyProgram.numberOfFirstTimeEnrolledPartTimeStudents || 0,
                    numberOfFirstTimeEnrolledDistanceLearningStudents: studyProgram.numberOfFirstTimeEnrolledDistanceLearningStudents || 0,
                    numberOfRepeatedlyEnrolledStudents: studyProgram.numberOfRepeatedlyEnrolledStudents || 0,
                    numberOfRepeatedlyEnrolledRegularStudents: studyProgram.numberOfRepeatedlyEnrolledRegularStudents || 0,
                    numberOfRepeatedlyEnrolledSelfFinancingStudents: studyProgram.numberOfRepeatedlyEnrolledSelfFinancingStudents || 0,
                    numberOfRepeatedlyEnrolledPartTimeStudents: studyProgram.numberOfRepeatedlyEnrolledPartTimeStudents || 0,
                    numberOfRepeatedlyEnrolledDistanceLearningStudents: studyProgram.numberOfRepeatedlyEnrolledDistanceLearningStudents || 0,
                };
            }
            const code = studyProgram.code && studyProgram.code.trim
                ? studyProgram.code.trim() : studyProgram.code;
            const implementationName = studyProgram.studyProgramName
                ? studyProgram.studyProgramName.replace(/\s*&\s*/g, ' i ') : '';
            const implementation = {
                name: implementationName || (code ? '' : '—'),
                code,
            };
            const implementationExists = studyProgramsByBasic[key].implementations.some(item =>
                item.name === implementation.name && item.code === implementation.code);
            if (!implementationExists) {
                studyProgramsByBasic[key].implementations.push(implementation);
            }
        });
        return Object.keys(studyProgramsByBasic).map(key => ({
            name: studyProgramsByBasic[key].name,
            implementations: studyProgramsByBasic[key].implementations,
            implementationsText: studyProgramsByBasic[key].implementations.map(implementation =>
                `${implementation.name} (${implementation.code || 'nema šifre'})`).join(' | '),
            numberOfStudents: studyProgramsByBasic[key].numberOfStudents,
            numberOfRegularStudents: studyProgramsByBasic[key].numberOfRegularStudents,
            numberOfSelfFinancingStudents: studyProgramsByBasic[key].numberOfSelfFinancingStudents,
            numberOfPartTimeStudents: studyProgramsByBasic[key].numberOfPartTimeStudents,
            numberOfDistanceLearningStudents: studyProgramsByBasic[key].numberOfDistanceLearningStudents,
            numberOfFirstTimeEnrolledStudents: studyProgramsByBasic[key].numberOfFirstTimeEnrolledStudents,
            numberOfFirstTimeEnrolledRegularStudents: studyProgramsByBasic[key].numberOfFirstTimeEnrolledRegularStudents,
            numberOfFirstTimeEnrolledSelfFinancingStudents: studyProgramsByBasic[key].numberOfFirstTimeEnrolledSelfFinancingStudents,
            numberOfFirstTimeEnrolledPartTimeStudents: studyProgramsByBasic[key].numberOfFirstTimeEnrolledPartTimeStudents,
            numberOfFirstTimeEnrolledDistanceLearningStudents: studyProgramsByBasic[key].numberOfFirstTimeEnrolledDistanceLearningStudents,
            numberOfRepeatedlyEnrolledStudents: studyProgramsByBasic[key].numberOfRepeatedlyEnrolledStudents,
            numberOfRepeatedlyEnrolledRegularStudents: studyProgramsByBasic[key].numberOfRepeatedlyEnrolledRegularStudents,
            numberOfRepeatedlyEnrolledSelfFinancingStudents: studyProgramsByBasic[key].numberOfRepeatedlyEnrolledSelfFinancingStudents,
            numberOfRepeatedlyEnrolledPartTimeStudents: studyProgramsByBasic[key].numberOfRepeatedlyEnrolledPartTimeStudents,
            numberOfRepeatedlyEnrolledDistanceLearningStudents: studyProgramsByBasic[key].numberOfRepeatedlyEnrolledDistanceLearningStudents,
        }));
    }

    getStudyTypeStatistics(studyProgram, enrollmentType) {
        if (enrollmentType === 'first') {
            return {
                total: studyProgram.numberOfFirstTimeEnrolledStudents,
                regular: studyProgram.numberOfFirstTimeEnrolledRegularStudents,
                selfFinancing: studyProgram.numberOfFirstTimeEnrolledSelfFinancingStudents,
                partTime: studyProgram.numberOfFirstTimeEnrolledPartTimeStudents,
                distanceLearning: studyProgram.numberOfFirstTimeEnrolledDistanceLearningStudents,
            };
        }
        if (enrollmentType === 'repeated') {
            return {
                total: studyProgram.numberOfRepeatedlyEnrolledStudents,
                regular: studyProgram.numberOfRepeatedlyEnrolledRegularStudents,
                selfFinancing: studyProgram.numberOfRepeatedlyEnrolledSelfFinancingStudents,
                partTime: studyProgram.numberOfRepeatedlyEnrolledPartTimeStudents,
                distanceLearning: studyProgram.numberOfRepeatedlyEnrolledDistanceLearningStudents,
            };
        }
        return {
            total: studyProgram.numberOfStudents,
            regular: studyProgram.numberOfRegularStudents,
            selfFinancing: studyProgram.numberOfSelfFinancingStudents,
            partTime: studyProgram.numberOfPartTimeStudents,
            distanceLearning: studyProgram.numberOfDistanceLearningStudents,
        };
    }

    getStudyTypeBreakdownText(studyProgram, enrollmentType) {
        const statistics = this.getStudyTypeStatistics(studyProgram, enrollmentType);
        return `Ukupno: ${statistics.total}\n` +
            `R: ${statistics.regular} | RSF: ${statistics.selfFinancing}\n` +
            `V: ${statistics.partTime} | DL: ${statistics.distanceLearning}`;
    }

    renderStudyTypeBreakdown(studyProgram, enrollmentType) {
        const statistics = this.getStudyTypeStatistics(studyProgram, enrollmentType);
        return this.renderStudyTypeStatistics(statistics);
    }

    renderStudyTypeStatistics(statistics) {
        const valueRows = [
            [['R', statistics.regular], ['RSF', statistics.selfFinancing]],
            [['V', statistics.partTime], ['DL', statistics.distanceLearning]],
        ];
        return (
            <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                <strong>Ukupno: {statistics.total}</strong>
                {valueRows.map((values, rowIndex) => (
                    <div key={`study-type-row-${rowIndex}`} style={{ marginTop: '3px', fontSize: '0.88em' }}>
                        {values.map((value, index) => (
                            <React.Fragment key={value[0]}>
                                {index > 0 && <span style={{ color: '#c7c7c7', margin: '0 5px' }}>|</span>}
                                <span style={{ color: '#777' }}>{value[0]}</span> {value[1]}
                            </React.Fragment>
                        ))}
                    </div>
                ))}
            </div>
        );
    }

    getCourseStudyTypeStatistics(course, enrollmentType) {
        const statistics = this.getStudyProgramRows(course)
            .map(studyProgram => this.getStudyTypeStatistics(studyProgram, enrollmentType))
            .reduce((total, current) => ({
                regular: total.regular + current.regular,
                selfFinancing: total.selfFinancing + current.selfFinancing,
                partTime: total.partTime + current.partTime,
                distanceLearning: total.distanceLearning + current.distanceLearning,
            }), {
                regular: 0, selfFinancing: 0, partTime: 0, distanceLearning: 0,
            });
        if (enrollmentType === 'first') {
            statistics.total = course.numberOfFirstTimeEnrolledStudents;
        } else if (enrollmentType === 'repeated') {
            statistics.total = course.numberOfRepeatedlyEnrolledStudents;
        } else {
            statistics.total = course.numberOfStudents;
        }
        return statistics;
    }

    getCourseStudyTypeBreakdownText(course, enrollmentType) {
        const statistics = this.getCourseStudyTypeStatistics(course, enrollmentType);
        return `Ukupno: ${statistics.total}\n` +
            `R: ${statistics.regular} | RSF: ${statistics.selfFinancing}\n` +
            `V: ${statistics.partTime} | DL: ${statistics.distanceLearning}`;
    }

    renderCourseStudyTypeBreakdown(course, enrollmentType) {
        return this.renderStudyTypeStatistics(this.getCourseStudyTypeStatistics(course, enrollmentType));
    }

    async printPdf() {
        const detailed = this.state.showDetailedStudentBreakdown;
        const dataToPrint = this.state.results.length ? this.state.results : this.state.data;
        const rows = dataToPrint.reduce((reportRows, course, courseIndex) => {
            const professorStatistics = course.professorStatistics && course.professorStatistics.length
                ? course.professorStatistics : [{ professor: '—', numberOfStudents: course.numberOfStudents }];
            const professorNames = professorStatistics.map((professor, index) => (
                course.hasGroups && professorStatistics.length > 1
                    ? `G${index + 1}: ${professor.professor}` : professor.professor
            )).join(' | ');
            const groupStudentNumbers = course.hasGroups && professorStatistics.length > 1
                ? professorStatistics
                    .map((professor, index) => `G${index + 1}: ${professor.numberOfStudents}`).join(' | ')
                : '—';
            const studyPrograms = this.getStudyProgramRows(course);
            return reportRows.concat(studyPrograms.map(studyProgram => ({
                courseId: course.courseBasicId,
                backgroundGroup: courseIndex,
                strongAlternate: true,
                course: course.courseBasicName,
                courseComment: course.courseBasicComment,
                studyProgramBasic: studyProgram.name,
                studyProgramImplementations: studyProgram.implementationsText,
                studyProgramStudents: this.getStudyTypeBreakdownText(studyProgram),
                studyProgramFirstEnrollmentStudents: this.getStudyTypeBreakdownText(studyProgram, 'first'),
                studyProgramRepeatedEnrollmentStudents: this.getStudyTypeBreakdownText(studyProgram, 'repeated'),
                professors: professorNames,
                totalStudents: detailed
                    ? this.getCourseStudyTypeBreakdownText(course) : course.numberOfStudents,
                groupStudents: groupStudentNumbers,
                firstEnrollment: detailed
                    ? this.getCourseStudyTypeBreakdownText(course, 'first')
                    : course.numberOfFirstTimeEnrolledStudents,
                repeatedEnrollment: detailed
                    ? this.getCourseStudyTypeBreakdownText(course, 'repeated')
                    : course.numberOfRepeatedlyEnrolledStudents,
            })));
        }, []);
        const filters = [
            {
                label: 'Akademska godina',
                value: this.getOptionText(this.state.academicYears, this.state.academicYearId),
            },
            {
                label: 'Organizaciona jedinica',
                value: this.getOptionText(
                    this.props.organizationalUnits,
                    this.state.organizationalUnitId
                ),
            },
            { label: 'Odsjek', value: this.getOptionText(this.state.departments, this.state.departmentId) },
            {
                label: 'Studijski program',
                value: this.getOptionText(this.state.studyProgramBasics, this.state.studyProgramBasicId),
            },
        ].filter(filter => filter.value);

        this.setState({ pdfLoading: true });
        try {
            await printReportAsPdf({
                title: 'Pregled nastavničkog opterećenja prema broju studenata',
                filters,
                columns: [
                    {
                        label: 'Predmet',
                        value: 'course',
                        width: 1.3,
                        mergeConsecutive: true,
                        mergeDisplayOnce: true,
                        mergeBy: row => row.courseId,
                        secondaryValue: 'courseComment',
                    },
                    {
                        label: 'Studijski programi na kojima se izvodi predmet',
                        value: 'studyProgramBasic',
                        width: 2.3,
                    },
                    {
                        label: 'Implementacije studijskih programa',
                        value: 'studyProgramImplementations',
                        width: 3.2,
                    },
                    {
                        label: 'Profesori zaduženi za predmet',
                        value: 'professors',
                        width: 1.2,
                        mergeConsecutive: true,
                        mergeDisplayOnce: true,
                        mergeBy: row => row.courseId,
                        textAlign: 'center',
                    },
                    {
                        label: 'Ukupno upisanih',
                        value: 'totalStudents',
                        width: 0.8,
                        mergeConsecutive: true,
                        mergeDisplayOnce: true,
                        mergeBy: row => row.courseId,
                        textAlign: 'center',
                        explicitLines: detailed,
                        fontSize: 5.5,
                        maxLines: 3,
                        lineHeight: 6,
                    },
                    detailed && {
                        label: 'Studenti po programu i tipu studiranja',
                        value: 'studyProgramStudents',
                        width: 1.4,
                        textAlign: 'center',
                        explicitLines: detailed,
                        fontSize: 5.5,
                        maxLines: 3,
                        lineHeight: 6,
                    },
                    {
                        label: 'Raspoređeni po grupama',
                        value: 'groupStudents',
                        width: 1,
                        mergeConsecutive: true,
                        mergeDisplayOnce: true,
                        mergeBy: row => row.courseId,
                        textAlign: 'center',
                    },
                    {
                        label: 'Prvi put upisani',
                        value: 'firstEnrollment',
                        width: 0.9,
                        mergeConsecutive: true,
                        mergeDisplayOnce: true,
                        mergeBy: row => row.courseId,
                        textAlign: 'center',
                        explicitLines: detailed,
                        fontSize: 5.5,
                        maxLines: 3,
                        lineHeight: 6,
                    },
                    detailed && {
                        label: 'Prvi put po programu i tipu',
                        value: 'studyProgramFirstEnrollmentStudents',
                        width: 1.3,
                        textAlign: 'center',
                        explicitLines: detailed,
                        fontSize: 5.5,
                        maxLines: 3,
                        lineHeight: 6,
                    },
                    {
                        label: 'Više puta upisani',
                        value: 'repeatedEnrollment',
                        width: 0.9,
                        mergeConsecutive: true,
                        mergeDisplayOnce: true,
                        mergeBy: row => row.courseId,
                        textAlign: 'center',
                        explicitLines: detailed,
                        fontSize: 5.5,
                        maxLines: 3,
                        lineHeight: 6,
                    },
                    detailed && {
                        label: 'Više puta po programu i tipu',
                        value: 'studyProgramRepeatedEnrollmentStudents',
                        width: 1.3,
                        textAlign: 'center',
                        explicitLines: true,
                        fontSize: 5.5,
                        maxLines: 3,
                        lineHeight: 6,
                    },
                ].filter(Boolean),
                rows,
                fileName: 'pregled-nastavnickog-opterecenja-prema-broju-studenata.pdf',
                titleColor: 'black',
            });
        } finally {
            this.setState({ pdfLoading: false });
        }
    }

    getData() {
        if (!this.state.academicYearId ||
            (this.props.requireOrganizationalUnit && !this.state.organizationalUnitId)) return;
        this.setState({ loading: true, reportRequested: true, activePage: 1 });
        axios.get(`/exams/course-students-by-academic-year/${this.state.academicYearId}/${this.state.studyProgramBasicId || 0}`, {
            params: {
                organizationalUnitId: this.state.organizationalUnitId || null,
                departmentId: this.state.departmentId || null,
                includeStudentBreakdown: this.state.showDetailedStudentBreakdown,
            },
        })
            .then(response => this.setState({
                data: response.data.map(course => ({
                    ...course,
                    studyProgramSearch: course.studyProgramBasics
                        .map(studyProgram => `${studyProgram.name} ${studyProgram.studyProgramName || ''} ${studyProgram.code || ''}`)
                        .join(' '),
                })),
                results: [],
                loading: false,
            }))
            .catch(() => this.setState({ data: [], results: [], loading: false }));
    }

    renderGroupedStudentNumbers(course, professorStatistics) {
        if (!course.hasGroups || professorStatistics.length <= 1) {
            return '—';
        }
        return (
            <div>
                <span
                    style={{
                        display: 'block', color: '#8a8a8a', fontSize: '0.82em', marginBottom: '5px',
                    }}
                >
                    Brojevi odgovaraju redoslijedu navedenih profesora
                </span>
                {professorStatistics.map((professor, index) => (
                    <React.Fragment key={`${professor.personId}-group-count`}>
                        {index > 0 && <span style={{ color: '#c7c7c7', margin: '0 8px' }}>|</span>}
                        <Popup
                            trigger={
                                <span
                                    style={{
                                        display: 'inline-block', cursor: 'help', textAlign: 'center',
                                    }}
                                >
                                    <span
                                        style={{
                                            display: 'block', color: '#999', fontSize: '0.72em', lineHeight: 1,
                                        }}
                                    >
                                        G{index + 1}
                                    </span>
                                    <span style={{ display: 'block', fontWeight: 600 }}>{professor.numberOfStudents}</span>
                                </span>
                            }
                            content={professor.professor}
                            position="top center"
                        />
                    </React.Fragment>
                ))}
            </div>
        );
    }

    renderCourseRows(course, courseIndex) {
        const studyPrograms = this.getStudyProgramRows(course);
        const professorStatistics = course.professorStatistics && course.professorStatistics.length
            ? course.professorStatistics
            : [{
                professor: '—',
                leadStaff: false,
                numberOfStudents: course.numberOfStudents,
                numberOfFirstTimeEnrolledStudents: course.numberOfFirstTimeEnrolledStudents,
                numberOfRepeatedlyEnrolledStudents: course.numberOfRepeatedlyEnrolledStudents,
            }];
        const rowCount = studyPrograms.length;
        const professorNames = professorStatistics.map(professor => professor.professor);
        const studentsDistributedInGroups = course.hasGroups && professorStatistics.length > 1;
        const courseHovered = this.state.hoveredCourseId === course.courseBasicId;
        const courseBackgroundColor = courseIndex % 2 === 0 ? '#fff' : 'rgba(0, 0, 0, 0.045)';
        const courseCellStyle = {
            ...columnBorderStyle,
            backgroundColor: courseHovered ? 'rgba(0, 0, 0, 0.085)' : courseBackgroundColor,
        };
        const rows = [];
        for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
            const studyProgram = studyPrograms[rowIndex];
            rows.push(<Table.Row
                key={`${course.courseBasicId}-${rowIndex}`}
                data-course-id={course.courseBasicId}
                onMouseEnter={() => this.setState({ hoveredCourseId: course.courseBasicId })}
                onMouseLeave={(event) => {
                    const nextElement = event.relatedTarget;
                    const nextRow = nextElement && nextElement.closest ? nextElement.closest('tr') : null;
                    if (!nextRow || nextRow.getAttribute('data-course-id') !== String(course.courseBasicId)) {
                        this.setState({ hoveredCourseId: null });
                    }
                }}
            >
                {rowIndex === 0 &&
                <Table.Cell
                    rowSpan={rowCount}
                    verticalAlign="middle"
                    style={courseCellStyle}
                >
                    {courseIndex + 1}. {course.courseBasicName}
                    {course.courseBasicComment &&
                    <span style={{ display: 'block', color: '#8a8a8a', marginTop: '3px' }}>
                        {course.courseBasicComment}
                    </span>}
                </Table.Cell>}
                <Table.Cell
                    verticalAlign="middle"
                    style={{
                        ...studyProgramCellStyle,
                        backgroundColor: courseHovered ? 'rgba(0, 0, 0, 0.085)' : courseBackgroundColor,
                    }}
                >
                    <span>{studyProgram.name}</span>
                </Table.Cell>
                <Table.Cell
                    verticalAlign="middle"
                    style={{
                        ...studyProgramCellStyle,
                        backgroundColor: courseHovered ? 'rgba(0, 0, 0, 0.085)' : courseBackgroundColor,
                    }}
                >
                    {studyProgram.implementations.map((implementation, index) => (
                        <React.Fragment key={`${course.courseBasicId}-${studyProgram.name}-${implementation.name}-${implementation.code}`}>
                            {index > 0 && <span style={{ color: '#c7c7c7', margin: '0 8px' }}>|</span>}
                            <span>{implementation.name}</span>
                            <span style={{ color: '#8a8a8a' }}>
                                {' '}({implementation.code || 'nema šifre'})
                            </span>
                        </React.Fragment>
                    ))}
                </Table.Cell>
                {rowIndex === 0 &&
                <Table.Cell textAlign="center" rowSpan={rowCount} verticalAlign="middle" style={courseCellStyle}>
                    {studentsDistributedInGroups &&
                    <span
                        style={{
                            display: 'block', color: '#8a8a8a', fontSize: '0.85em', marginBottom: '5px',
                        }}
                    >
                        Studenti su raspoređeni u grupe
                    </span>}
                    {professorNames.map((professor, index) => (
                        <React.Fragment key={`${course.courseBasicId}-professor-${index}`}>
                            {index > 0 && <span style={{ color: '#c7c7c7', margin: '0 8px' }}>|</span>}
                            <span style={{ display: 'inline-block', textAlign: 'center' }}>
                                {studentsDistributedInGroups &&
                                <span
                                    style={{
                                        display: 'block', color: '#999', fontSize: '0.72em', lineHeight: 1,
                                    }}
                                >
                                    G{index + 1}
                                </span>}
                                <span>{professor}</span>
                            </span>
                        </React.Fragment>
                    ))}
                </Table.Cell>}
                {rowIndex === 0 &&
                <Table.Cell rowSpan={rowCount} textAlign="center" verticalAlign="middle" style={courseCellStyle}>
                    {this.state.showDetailedStudentBreakdown
                        ? this.renderCourseStudyTypeBreakdown(course) : course.numberOfStudents}
                </Table.Cell>}
                {this.state.showDetailedStudentBreakdown &&
                <Table.Cell
                    verticalAlign="middle"
                    style={{
                        ...studyProgramCellStyle,
                        backgroundColor: courseHovered ? 'rgba(0, 0, 0, 0.085)' : courseBackgroundColor,
                    }}
                >
                    {this.renderStudyTypeBreakdown(studyProgram)}
                </Table.Cell>}
                {rowIndex === 0 &&
                <Table.Cell rowSpan={rowCount} textAlign="center" verticalAlign="middle" style={courseCellStyle}>
                    {this.renderGroupedStudentNumbers(course, professorStatistics)}
                </Table.Cell>}
                {rowIndex === 0 &&
                <Table.Cell rowSpan={rowCount} textAlign="center" verticalAlign="middle" style={courseCellStyle}>
                    {this.state.showDetailedStudentBreakdown
                        ? this.renderCourseStudyTypeBreakdown(course, 'first')
                        : course.numberOfFirstTimeEnrolledStudents}
                </Table.Cell>}
                {this.state.showDetailedStudentBreakdown &&
                <Table.Cell
                    verticalAlign="middle"
                    style={{
                        ...studyProgramCellStyle,
                        backgroundColor: courseHovered ? 'rgba(0, 0, 0, 0.085)' : courseBackgroundColor,
                    }}
                >
                    {this.renderStudyTypeBreakdown(studyProgram, 'first')}
                </Table.Cell>}
                {rowIndex === 0 &&
                <Table.Cell rowSpan={rowCount} textAlign="center" verticalAlign="middle" style={courseCellStyle}>
                    {this.state.showDetailedStudentBreakdown
                        ? this.renderCourseStudyTypeBreakdown(course, 'repeated')
                        : course.numberOfRepeatedlyEnrolledStudents}
                </Table.Cell>}
                {this.state.showDetailedStudentBreakdown &&
                <Table.Cell
                    verticalAlign="middle"
                    style={{
                        ...studyProgramCellStyle,
                        backgroundColor: courseHovered ? 'rgba(0, 0, 0, 0.085)' : courseBackgroundColor,
                    }}
                >
                    {this.renderStudyTypeBreakdown(studyProgram, 'repeated')}
                </Table.Cell>}
            </Table.Row>,);
        }
        return rows;
    }

    render() {
        const detailed = this.state.showDetailedStudentBreakdown;
        const dataToRender = this.state.results.length ? this.state.results : this.state.data;
        const firstIndex = (this.state.activePage - 1) * this.state.postsPerPage;
        const currentCourses = dataToRender.slice(firstIndex, firstIndex + this.state.postsPerPage);
        const excelData = dataToRender.reduce((rows, course) => {
            const professorStatistics = course.professorStatistics && course.professorStatistics.length
                ? course.professorStatistics
                : [{
                    professor: '—',
                    leadStaff: false,
                    numberOfStudents: course.numberOfStudents,
                    numberOfFirstTimeEnrolledStudents: course.numberOfFirstTimeEnrolledStudents,
                    numberOfRepeatedlyEnrolledStudents: course.numberOfRepeatedlyEnrolledStudents,
                }];
            const professorNames = professorStatistics.map(professor => professor.professor).join(' | ');
            const studentNumbers = professorStatistics
                .map((professor, index) => `G${index + 1}: ${professor.numberOfStudents}`).join(' | ');
            const studyPrograms = this.getStudyProgramRows(course);
            return rows.concat(studyPrograms.map(studyProgram => ([
                { value: `${course.courseBasicName}${course.courseBasicComment ? `\n${course.courseBasicComment}` : ''}` },
                { value: studyProgram.name },
                { value: studyProgram.implementationsText },
                { value: professorNames },
                { value: detailed ? this.getCourseStudyTypeBreakdownText(course) : course.numberOfStudents },
                detailed && { value: this.getStudyTypeBreakdownText(studyProgram) },
                { value: course.hasGroups && professorStatistics.length > 1 ? studentNumbers : '' },
                {
                    value: detailed
                        ? this.getCourseStudyTypeBreakdownText(course, 'first')
                        : course.numberOfFirstTimeEnrolledStudents
                },
                detailed && { value: this.getStudyTypeBreakdownText(studyProgram, 'first') },
                {
                    value: detailed
                        ? this.getCourseStudyTypeBreakdownText(course, 'repeated')
                        : course.numberOfRepeatedlyEnrolledStudents
                },
                detailed && { value: this.getStudyTypeBreakdownText(studyProgram, 'repeated') },
            ].filter(Boolean))));
        }, []);
        const excelDataSet = [{
            columns: [
                { title: 'Predmet', width: { wpx: 300 } },
                { title: 'Studijski programi na kojima se izvodi predmet', width: { wpx: 750 } },
                { title: 'Implementacije studijskih programa', width: { wpx: 500 } },
                { title: 'Profesori zaduženi za predmet', width: { wpx: 260 } },
                { title: 'Ukupan broj studenata upisanih na predmet', width: { wpx: 240 } },
                detailed && { title: 'Studenti po studijskom programu i tipu studiranja', width: { wpx: 300 } },
                { title: 'Broj studenata raspoređenih po grupama', width: { wpx: 260 } },
                { title: 'Broj prvi put upisanih studenata', width: { wpx: 220 } },
                detailed && { title: 'Prvi put upisani po studijskom programu i tipu studiranja', width: { wpx: 300 } },
                { title: 'Broj studenata koji su predmet upisivali više puta', width: { wpx: 260 } },
                detailed && { title: 'Više puta upisani po studijskom programu i tipu studiranja', width: { wpx: 300 } },
            ].filter(Boolean),
            data: excelData,
        }];

        return (
            <div>
                <Form>
                    <Segment style={requiredFilterSectionStyle}>
                        <Header as="h4" color="blue">Obavezni parametri</Header>
                        <Form.Group widths="equal">
                            <Form.Dropdown
                                label="Akademska godina"
                                placeholder="Odaberite akademsku godinu"
                                name="academicYearId"
                                value={this.state.academicYearId}
                                options={this.state.academicYears}
                                onChange={this.handleInputChange}
                                search
                                fluid
                                selection
                            />
                            {this.props.requireOrganizationalUnit ? <Form.Dropdown
                                label="Organizaciona jedinica"
                                placeholder="Odaberite organizacionu jedinicu"
                                name="organizationalUnitId"
                                value={this.state.organizationalUnitId}
                                options={this.props.organizationalUnits}
                                onChange={this.handleInputChange}
                                search
                                fluid
                                selection
                            /> : <Form.Field />}
                        </Form.Group>
                    </Segment>
                    <Segment style={additionalFilterSectionStyle}>
                        <Header as="h4" color="blue">Dodatni filteri</Header>
                        <Form.Group style={filterFieldGridStyle}>
                            <Form.Dropdown
                                label="Odsjek"
                                placeholder="Odaberite odsjek"
                                name="departmentId"
                                value={this.state.departmentId}
                                options={this.state.departments}
                                onChange={this.handleInputChange}
                                clearable
                                search
                                fluid
                                selection
                            />
                            <Form.Dropdown
                                label="Studijski program"
                                placeholder="Odaberite studijski program"
                                name="studyProgramBasicId"
                                value={this.state.studyProgramBasicId}
                                options={this.state.studyProgramBasics}
                                onChange={this.handleInputChange}
                                clearable
                                search
                                fluid
                                selection
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Checkbox
                                style={checkboxFieldStyle}
                                label="Prikaži detaljnu raspodjelu studenata po studijskom programu i tipu studiranja"
                                checked={this.state.showDetailedStudentBreakdown}
                                onChange={(event, data) => this.setState({
                                    showDetailedStudentBreakdown: data.checked,
                                    data: [],
                                    results: [],
                                    reportRequested: false,
                                    activePage: 1,
                                })}
                            />
                        </Form.Group>
                    </Segment>
                    <Button basic color="blue" floated="right" onClick={this.props.closeReport}>Odustani</Button>
                    <Button
                        color="blue"
                        floated="right"
                        onClick={this.getData}
                        disabled={!this.state.academicYearId ||
                            (this.props.requireOrganizationalUnit && !this.state.organizationalUnitId)}
                    >
                        Prikaži izvještaj
                    </Button>
                </Form>

                <br /><br /><br />

                {this.state.loading &&
                    <div style={{ paddingTop: '100px', paddingBottom: '100px' }}>
                        <Loader active inline="centered" />
                    </div>}

                {!this.state.loading && this.state.reportRequested && this.state.data.length === 0 &&
                    <p>Nema podataka za odabrane parametre.</p>}

                {!this.state.loading && this.state.data.length > 0 &&
                    <Table
                        celled
                        compact
                        size="small"
                        style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}
                    >
                        <colgroup>
                            {detailed ? <React.Fragment>
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '16%' }} />
                                <col style={{ width: '11%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '8%' }} />
                            </React.Fragment> : <React.Fragment>
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '17%' }} />
                                <col style={{ width: '22%' }} />
                                <col style={{ width: '16%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '8%' }} />
                            </React.Fragment>}
                        </colgroup>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell colSpan={detailed ? 11 : 8}>
                                    <Grid columns={3} stackable>
                                        <Grid.Column width={12}>
                                            <SearchTableData
                                                profMozilla
                                                data={this.state.data}
                                                getResults={this.getResults}
                                                filterItem={['courseBasicName', 'courseBasicComment', 'studyProgramSearch', 'professors']}
                                            />
                                        </Grid.Column>
                                        <Grid.Column width={2} textAlign="center">
                                            <ExcelFile
                                                filename="Pregled nastavnog opterećenja prema broju studenata"
                                                element={
                                                    <Popup
                                                        trigger={<Button animated="vertical" color="green" basic fluid>
                                                            <Button.Content hidden><Icon name="download" /></Button.Content>
                                                            <Button.Content visible>Excel</Button.Content>
                                                        </Button>}
                                                        content="Preuzmi izvještaj u Excel-u"
                                                        position="top center"
                                                    />
                                                }
                                            >
                                                <ExcelSheet dataSet={excelDataSet} name="Opterećenje nastavnika" />
                                            </ExcelFile>
                                        </Grid.Column>
                                        <Grid.Column width={2} textAlign="center">
                                            <Popup
                                                trigger={<Button
                                                    animated="vertical"
                                                    color="red"
                                                    basic
                                                    fluid
                                                    loading={this.state.pdfLoading}
                                                    disabled={this.state.pdfLoading}
                                                    onClick={this.printPdf}
                                                >
                                                    <Button.Content hidden><Icon name="download" /></Button.Content>
                                                    <Button.Content visible>PDF</Button.Content>
                                                </Button>}
                                                content="Preuzmi izvještaj u PDF-u"
                                                position="top center"
                                            />
                                        </Grid.Column>
                                    </Grid>
                                </Table.HeaderCell>
                            </Table.Row>
                            <Table.Row>
                                <Table.HeaderCell style={columnBorderStyle}>Predmet</Table.HeaderCell>
                                <Table.HeaderCell style={columnBorderStyle}>Studijski programi na kojima se izvodi predmet</Table.HeaderCell>
                                <Table.HeaderCell style={columnBorderStyle}>Implementacije studijskih programa</Table.HeaderCell>
                                <Table.HeaderCell style={columnBorderStyle} textAlign="center">Profesori zaduženi za predmet</Table.HeaderCell>
                                <Table.HeaderCell style={columnBorderStyle} textAlign="center">Ukupan broj studenata upisanih na predmet</Table.HeaderCell>
                                {detailed &&
                                <Table.HeaderCell style={columnBorderStyle} textAlign="center">Studenti po studijskom programu i tipu studiranja</Table.HeaderCell>
                                }
                                <Table.HeaderCell style={columnBorderStyle} textAlign="center">Broj studenata raspoređenih po grupama</Table.HeaderCell>
                                <Table.HeaderCell style={columnBorderStyle} textAlign="center">Broj prvi put upisanih studenata</Table.HeaderCell>
                                {detailed &&
                                <Table.HeaderCell style={columnBorderStyle} textAlign="center">Prvi put upisani po studijskom programu i tipu studiranja</Table.HeaderCell>
                                }
                                <Table.HeaderCell style={columnBorderStyle} textAlign="center">Broj studenata koji su predmet upisivali više puta</Table.HeaderCell>
                                {detailed &&
                                <Table.HeaderCell style={columnBorderStyle} textAlign="center">Više puta upisani po studijskom programu i tipu studiranja</Table.HeaderCell>
                                }
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {currentCourses.reduce((rows, course, index) => rows.concat(this.renderCourseRows(course, firstIndex + index),), [])}
                        </Table.Body>
                        <Table.Footer>
                            <Table.Row>
                                <Table.HeaderCell colSpan={detailed ? 11 : 8}>
                                    <PaginationFront
                                        postsPerPage={this.state.postsPerPage}
                                        totalPosts={dataToRender.length}
                                        paginate={(event, data) => this.setState({ activePage: data.activePage })}
                                        activePage={this.state.activePage}
                                    />
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Footer>
                    </Table>}
            </div>
        );
    }
}

export default CourseStudentsByAcademicYear;



// WEBPACK FOOTER //
// ./src/views/StudentService/StudentsReports/CourseStudentsByAcademicYear.js